import { createContext, ReactNode, useContext, useMemo } from "react";
import { ProductListConfig } from "../../types/types";

/**
 * Stable, card-wide identifiers that never change for the lifetime of a
 * ProductCard render (config/instanceId/consumerId are threaded down from
 * ProductList and never mutated in place). Kept in their own context so that
 * VariantContext and ProductCardEventContext — which own genuinely
 * per-render/variant state — don't each need to accept and re-forward these
 * as props.
 */
type ProductCardMetadata = {
  config: ProductListConfig;
  instanceId?: string;
  consumerId: string;
};

const ProductCardMetadataContext = createContext<ProductCardMetadata | null>(
  null,
);

type Props = ProductCardMetadata & {
  children: ReactNode;
};

export function ProductCardMetadataProvider({
  children,
  config,
  instanceId,
  consumerId,
}: Props): React.JSX.Element {
  // Memoised so consumers only re-render when one of these values actually
  // changes, rather than on every ProductCard render.
  const value = useMemo(
    () => ({ config, instanceId, consumerId }),
    [config, instanceId, consumerId],
  );

  return (
    <ProductCardMetadataContext.Provider value={value}>
      {children}
    </ProductCardMetadataContext.Provider>
  );
}

export function useProductCardMetadata(): ProductCardMetadata {
  const context = useContext(ProductCardMetadataContext);

  if (!context) {
    throw new Error(
      "useProductCardMetadata must be used within a ProductCardMetadataProvider",
    );
  }

  return context;
}
